/**
 * Le remplissage des imprimés officiels : Cerfa de CFE, demande d'ACRE, et
 * ceux qui suivront.
 *
 * Séparé en deux : `remplirFormulaire` prend les octets de l'imprimé et rend
 * ceux du PDF rempli, sans rien savoir du navigateur ; `telechargerFormulaire`
 * va chercher l'imprimé et remet le résultat au lecteur. C'est ce qui permet à
 * un harnais de remplir le formulaire pour de bon et de RELIRE ses cases,
 * plutôt que de faire confiance à une table de correspondance jamais exécutée.
 *
 * LES IMPRIMÉS NE SONT PAS APLATIS. Nous ne remplissons que ce que le parcours
 * demande, et le lecteur doit pouvoir compléter le reste dans son lecteur de
 * PDF. Aplatir figerait le document et lui retirerait cette main.
 *
 * Quatre sortes d'entrées, toutes facultatives :
 *   - `textes`  : { nomDeChamp: "valeur" }
 *   - `cases`   : [ "nomDeCase" ], cochées avec la valeur de leur widget
 *   - `choix`   : { nomDeCase: "valeur" }, pour les cases à plusieurs widgets
 *   - `radios`  : { nomDuGroupe: "option" }
 * Et une image de signature, posée par-dessus la page à l'endroit indiqué par
 * `champs.signature` : un champ PDFSignature ne se remplit pas comme un texte,
 * et un paraphe dessiné à la souris n'est pas une signature numérique, c'est
 * une image, qui se dessine là où l'imprimé attend le trait.
 *
 * pdf-lib est chargé à la demande : il pèse près d'un mégaoctet, et la page se
 * lit sans jamais l'appeler.
 */

export async function remplirFormulaire(champs, imprime, signature = "") {
    const { PDFDocument, PDFName, StandardFonts, rgb } = await import("pdf-lib");
    const pdf = await PDFDocument.load(imprime);
    const formulaire = pdf.getForm();

    const poses = [];
    const introuvables = [];

    for (const [nom, valeur] of Object.entries(champs.textes)) {
        try {
            formulaire.getTextField(nom).setText(String(valeur ?? ""));
            poses.push(nom);
        } catch {
            introuvables.push(nom);
        }
    }

    /* Toutes les cases se posent au niveau du dictionnaire, jamais par
       `check()`. La raison est dans les cases à plusieurs widgets, OUI ou NON
       sur un même champ, « début d'activité » ou « transfert » sur un autre :
       pdf-lib ne connaît qu'une valeur par case, celle de son premier widget,
       et refuse les autres. Poser la valeur du champ et l'état de chaque
       widget couvre les deux formes, et une seule méthode vaut mieux que deux.
       (Le rendu montre trois cases avec une croix grise plutôt qu'une coche :
       c'est le dessin de l'imprimé lui-même, vérifié en comparant les deux
       méthodes de pose.) */
    const poserCase = (nom, valeur) => {
        const acro = formulaire.getCheckBox(nom).acroField;
        const widgets = acro.getWidgets();
        // Une case simple prend la valeur de son unique widget.
        const cible = valeur ?? widgets[0]?.getOnValue()?.toString().slice(1);
        if (!cible) throw new Error(`${nom} sans valeur`);
        acro.dict.set(PDFName.of("V"), PDFName.of(cible));
        for (const widget of widgets) {
            const on = widget.getOnValue();
            widget.setAppearanceState(
                on && on.toString() === `/${cible}` ? on : PDFName.of("Off"),
            );
        }
    };

    for (const nom of champs.cases) {
        try {
            poserCase(nom);
            poses.push(nom);
        } catch {
            introuvables.push(nom);
        }
    }
    for (const [nom, valeur] of Object.entries(champs.choix || {})) {
        try {
            poserCase(nom, valeur);
            poses.push(nom);
        } catch {
            introuvables.push(nom);
        }
    }

    for (const [nom, option] of Object.entries(champs.radios || {})) {
        try {
            formulaire.getRadioGroup(nom).select(String(option));
            poses.push(nom);
        } catch {
            introuvables.push(nom);
        }
    }

    /* Sans cette passe, certains lecteurs affichent des cases vides sur un
       formulaire pourtant rempli. La POLICE est fournie, et ce n'est pas un
       détail : les champs du 14022 déclarent une police par défaut,
       « /Helvetica », que les ressources du document ne contiennent pas. Sans
       police explicite, pdf-lib écrivait des apparences qui pointaient dans le
       vide, et l'imprimé sortait VISUELLEMENT VIDE alors que chaque valeur se
       relisait correctement. Aucun contrôle de valeur ne pouvait le voir. */
    formulaire.updateFieldAppearances(await pdf.embedFont(StandardFonts.Helvetica));

    /* Une croix PEINTE sur la page, en plus de la valeur du champ.
       Certains imprimés portent une couche XFA que pdf-lib doit retirer, et
       leurs cases perdent alors leur dessin : la valeur est posée, le lecteur
       n'affiche rien, et aucun contrôle de valeur ne peut le voir. Vérifié sur
       le 14022, où même `check()` de pdf-lib laisse la case vide à l'écran.
       La marque est donc dessinée dans le contenu de la page, où aucun lecteur
       ne peut l'ignorer. Les imprimés dont les cases s'affichent seules n'en
       demandent pas et n'en reçoivent pas.

       LA POSITION EST D'ABORD DÉGAGÉE, et sans cela la croix reste invisible :
       les annotations se peignent APRÈS le contenu de la page, et le 14022
       superpose à chaque case deux widgets, une case à cocher et un bouton
       radio, dont l'état « décoché » est un carré blanc plein. Ce carré
       recouvrait la croix, ce qui donnait un imprimé où tout s'affichait sauf
       la seule chose qu'on avait dessinée. Le cadre de la case, lui, appartient
       au fond de page et survit au retrait. */
    for (const { page: numero, x, y, cote = 10 } of champs.marques || []) {
        const page = pdf.getPage(numero - 1);
        const annotations = page.node.Annots();
        for (let rang = (annotations?.size() ?? 0) - 1; rang >= 0; rang--) {
            const annotation = pdf.context.lookup(annotations.get(rang));
            const rect = annotation?.get?.(PDFName.of("Rect"));
            if (!rect) continue;
            const proche =
                Math.abs(rect.get(0).asNumber() - x) < 2 && Math.abs(rect.get(1).asNumber() - y) < 2;
            if (proche) annotations.remove(rang);
        }
        const marge = cote * 0.22;
        const trait = { thickness: 1.1, color: rgb(0, 0, 0) };
        page.drawLine({
            start: { x: x + marge, y: y + marge },
            end: { x: x + cote - marge, y: y + cote - marge },
            ...trait,
        });
        page.drawLine({
            start: { x: x + marge, y: y + cote - marge },
            end: { x: x + cote - marge, y: y + marge },
            ...trait,
        });
        poses.push("marque");
    }

    /* La signature est une image posée sur la page, pas une valeur de champ.
       Elle garde ses proportions et tient dans la zone, calée à gauche : un
       trait étiré en largeur ne ressemble plus à celui de son auteur. */
    if (signature && champs.signature) {
        const { page: numero, x, y, largeur, hauteur } = champs.signature;
        const image = await pdf.embedPng(signature);
        const echelle = Math.min(largeur / image.width, hauteur / image.height);
        pdf.getPage(numero - 1).drawImage(image, {
            x,
            y,
            width: image.width * echelle,
            height: image.height * echelle,
        });
        poses.push("signature");
    }

    return { octets: await pdf.save(), poses, introuvables };
}

/** Va chercher l'imprimé vierge, le remplit, et le remet au navigateur. */
export async function telechargerFormulaire({ champs, nomFichier, signature = "" }) {
    if (!champs) return;
    const reponse = await fetch(champs.millesime.fichier);
    if (!reponse.ok) throw new Error(`Imprimé introuvable : ${champs.millesime.fichier}`);

    const { octets } = await remplirFormulaire(champs, await reponse.arrayBuffer(), signature);

    const lien = document.createElement("a");
    lien.href = URL.createObjectURL(new Blob([octets], { type: "application/pdf" }));
    lien.download = nomFichier;
    lien.click();
    URL.revokeObjectURL(lien.href);
}
